import { useLayoutEffect, useRef } from "react";
import type { ReactNode } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

export function MotionLayer({ children, routeKey }: { children: ReactNode; routeKey: string }) {
  const root = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const context = gsap.context(() => {
      const media = gsap.matchMedia();

      media.add("(prefers-reduced-motion: no-preference)", () => {
        const heroLines = gsap.utils.toArray<HTMLElement>("[data-hero-line] > span");
        const heroSupport = gsap.utils.toArray<HTMLElement>("[data-hero-support]");
        const heroVisual = gsap.utils.toArray<HTMLElement>("[data-hero-visual]");
        const hero = gsap.timeline({ defaults: { ease: "power3.out" } });
        if (heroLines.length) {
          hero.from(heroLines, {
            yPercent: 112,
            rotate: 2,
            stagger: 0.07,
            duration: 0.92,
          });
        }
        if (heroSupport.length) {
          hero.from(heroSupport, {
            autoAlpha: 0,
            y: 24,
            stagger: 0.07,
            duration: 0.62,
          }, heroLines.length ? "-=0.56" : 0);
        }
        if (heroVisual.length) {
          hero.from(heroVisual, {
            autoAlpha: 0,
            y: 42,
            duration: 1.2,
            clearProps: "opacity,transform,visibility",
          }, heroLines.length || heroSupport.length ? "-=0.76" : 0);
        }

        gsap.utils.toArray<HTMLElement>("[data-reveal]").forEach((element) => {
          gsap.from(element, {
            autoAlpha: 0,
            y: 54,
            duration: 0.86,
            ease: "power3.out",
            scrollTrigger: {
              trigger: element,
              start: "top 88%",
              once: true,
            },
          });
        });

        gsap.utils.toArray<HTMLElement>("[data-parallax]").forEach((element) => {
          gsap.fromTo(element, { scale: 1.08, yPercent: -2 }, {
            scale: 1.02,
            yPercent: 5,
            ease: "none",
            scrollTrigger: {
              trigger: element.parentElement,
              start: "top bottom",
              end: "bottom top",
              scrub: 0.8,
            },
          });
        });

        gsap.utils.toArray<HTMLElement>("[data-drift-line]").forEach((element, index) => {
          gsap.fromTo(element, { xPercent: index % 2 === 0 ? -4 : 4 }, {
            xPercent: index % 2 === 0 ? 4 : -4,
            ease: "none",
            scrollTrigger: {
              trigger: element,
              start: "top bottom",
              end: "bottom top",
              scrub: 1,
            },
          });
        });

        gsap.utils.toArray<HTMLElement>("[data-route-object]").forEach((element) => {
          gsap.fromTo(element, {
            autoAlpha: 0,
          }, {
            autoAlpha: 1,
            duration: 1.15,
            ease: "power3.out",
            clearProps: "opacity,visibility",
          });
        });

        gsap.utils.toArray<HTMLElement>("[data-route-pan]").forEach((element) => {
          gsap.fromTo(element, { xPercent: 7 }, {
            xPercent: -7,
            ease: "none",
            scrollTrigger: {
              trigger: element.parentElement,
              start: "top bottom",
              end: "bottom top",
              scrub: 1,
            },
          });
        });

        gsap.utils.toArray<HTMLElement>("[data-clip-reveal]").forEach((element) => {
          gsap.from(element, {
            clipPath: "inset(100% 0 0 0)",
            duration: 1.1,
            ease: "power4.inOut",
            scrollTrigger: {
              trigger: element,
              start: "top 86%",
              once: true,
            },
          });
        });
      });

      return () => media.revert();
    }, root);

    ScrollTrigger.refresh();
    return () => context.revert();
  }, [routeKey]);

  return <div ref={root} className="route-stage" key={routeKey}>{children}</div>;
}
